'use client';
import { useClickOutside, useInViewport } from '@mantine/hooks';
import { DateTime } from 'luxon';
import Image from 'next/image';
import React, { useMemo, useState } from 'react';

import { Loader } from '@mantine/core';
import { SystemNotification } from '@scaleup/models';
import {
  useGetNotificationsQuery,
  useGetOrgProfileImageQuery,
  useMarkAsAcknowledgedMutation,
  useMarkasViewedMutation,
} from '../api';
import { PLACEHOLDER_PROFILE_IMAGE } from '../constants';
import { safeImage } from '../utils';

interface NotificationProps {
  notification: SystemNotification;
}

const NotificationItem: React.FC<NotificationProps> = ({ notification }) => {
  const { ref, inViewport } = useInViewport();
  const [markasViewed] = useMarkasViewedMutation();
  const [markAsAcknowledged] = useMarkAsAcknowledgedMutation();
  const { data: profileImageData, isLoading: isProfileLoading } =
    useGetOrgProfileImageQuery(notification.secondaryOrg || notification.orgId);

  const timeAgo = DateTime.fromSeconds(notification.created)
    .diffNow()
    .as('hours');

  const timeString = timeAgo < 1 ? 'Just Now' : `${Math.abs(timeAgo)} hour ago`;

  return (
    <a
      role="button"
      onMouseEnter={() => markasViewed(notification.notificationId)}
      onClick={() => markAsAcknowledged(notification.notificationId)}
      ref={ref}
      className="relative transition-colors hover:bg-secondary-200 bg-white flex flex-row justify-between m-2 p-2 rounded-xl gap-3 items-start"
      key={notification.notificationId}
    >
      <div className="flex gap-3 items-start">
        {isProfileLoading ? (
          <Loader />
        ) : (
          <Image
            className="rounded-full"
            src={
              safeImage(profileImageData?.image) || PLACEHOLDER_PROFILE_IMAGE
            }
            height={40}
            width={40}
            alt="Avatar"
          />
        )}
        <div className="flex flex-col gap-2">
          <h4 className="font-bold text-text-primary text-base">
            {notification.notificationType}
          </h4>
          <p className="text-text-secondary text-base font-normal">
            {notification.systemMessage}
          </p>
          <p className="text-text-secondary text-xs font-normal">
            {timeString}
          </p>
        </div>
      </div>
      {!notification.viewed && (
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-error-500 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-error-500"></span>
        </span>
      )}
    </a>
  );
};

export const NotificationDropDown = () => {
  const { data: notifications } = useGetNotificationsQuery();
  const [markAsAcknowledged] = useMarkAsAcknowledgedMutation();
  const [showUnreadOnly, setShowUnreadOnly] = useState<boolean>(false);
  const filteredNotifications = useMemo<SystemNotification[]>(() => {
    // filter by read when property is available
    return (notifications || []).filter((not) => !not.acknowledged);
  }, [notifications]);

  const [open, setOpen] = useState<boolean>(false);
  const ref = useClickOutside(() => setOpen(false));

  const handleMarkAllAsAcknowledged = () => {
    const unreadNotifications = filteredNotifications?.filter(
      (notification) => !notification.acknowledged
    );
    for (const notification of unreadNotifications || []) {
      markAsAcknowledged(notification.notificationId);
    }
  };

  return (
    <div className="px-4 py-3 rounded-[100px] bg-white/75 relative after:content-[''] after:w-[452px] after:right-0 after:h-[20px] after:absolute">
      <Image
        src="/icons/notification.svg"
        alt="Notification On"
        width={24}
        height={24}
        className="cursor-pointer"
        onClick={() => setOpen(!open)}
      />
      {filteredNotifications.some((notification) => !notification.viewed) && (
        <span className="absolute top-1 right-3">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-error-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-error-500"></span>
          </span>
        </span>
      )}
      <div
        ref={ref}
        className={`rounded-[1rem] shadow-notification absolute bg-white w-[452px] right-0 top-[54px] z-10 ${open ? 'block' : 'hidden'}`}
      >
        <div className="flex flex-row border-b p-4 justify-between">
          <h2 className="text-text-primary font-bold text-xl">Notifications</h2>
          {filteredNotifications.some(
            (notification) => !notification.acknowledged
          ) && (
            <div className="flex gap-2 items-center">
              <span className="text-text-secondary text-[14px]">
                Only show unread
              </span>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  onChange={(e) => setShowUnreadOnly(e.target.checked)}
                />
                <div className="relative w-[36px] h-[20px] bg-gray-200 rounded-[12px] peer peer-focus:ring-1 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-[16px] after:w-[16px] after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
              </label>
            </div>
          )}
        </div>

        {filteredNotifications.some(
          (notification) => !notification.acknowledged
        ) && (
          <div className="flex flex-row p-4 justify-end">
            <button
              className="text-primary-700 font-medium"
              onClick={handleMarkAllAsAcknowledged}
            >
              Mark all as acknowledged
            </button>
          </div>
        )}
        <div className="max-h-[300px] overflow-y-scroll">
          {filteredNotifications.map((notification) => (
            <NotificationItem
              key={notification.notificationId}
              notification={notification}
            />
          ))}
          {filteredNotifications?.length === 0 && (
            <div className="relative flex flex-row justify-between p-4 gap-3 items-start">
              <span>No notifications</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
